import React, { useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../hooks/useAuth";

const Label = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="font-sans uppercase text-navy/70 block mb-3"
    style={{ fontSize: "11px", letterSpacing: "0.22em" }}
  >
    {children}
  </label>
);

const Input = ({ as: As = "input", id, error, ...rest }) => (
  <As
    id={id}
    aria-invalid={!!error}
    className={`w-full bg-transparent border ${
      error ? "border-navy/40" : "border-navy/18"
    } px-4 py-4 font-sans text-[16px] text-navy focus:outline-none focus:ring-0 focus:border-navy transition-colors duration-300`}
    style={{ minHeight: As === "textarea" ? 200 : 56, borderRadius: 2 }}
    {...rest}
  />
);

const FieldError = ({ id, children }) =>
  children ? (
    <p id={id} className="mt-2 font-sans text-navy/75" style={{ fontSize: "12px" }}>
      {children}
    </p>
  ) : null;

const initial = { name: "", company: "", email: "", phone: "", subject: "", message: "", website: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "A name is required.";
    if (!form.company.trim()) e.company = "A company is required.";
    if (!form.email.trim()) e.email = "An email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "A valid email is required.";
    if (!form.subject.trim()) e.subject = "A subject is required.";
    if (!form.message.trim()) e.message = "A message is required.";
    else if (form.message.trim().length < 12) e.message = "Please provide a more complete message.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError(null);
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) return;
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/contact", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          subject: form.subject.trim(),
          message: form.message.trim(),
          website: form.website,
        }),
      });
      if (!res.ok) {
        setServerError("We were unable to submit your inquiry at this time. Please try again later.");
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch {
      setServerError("We were unable to submit your inquiry at this time. Please try again later.");
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="flex flex-col items-center text-center py-10">
        <span aria-hidden="true" className="block h-px w-12 bg-gold" />
        <span
          className="mt-8 font-sans uppercase text-gold"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          Received
        </span>
        <h3
          className="mt-6 font-serif font-normal text-navy"
          style={{ fontSize: "clamp(28px, 3vw, 40px)", lineHeight: 1.15 }}
        >
          Thank you for your inquiry.
        </h3>
        <p className="mt-5 font-serif italic text-navy/70" style={{ fontSize: "18px" }}>
          Your message has been received. The firm will respond as appropriate.
        </p>
        <Link
          to="/"
          className="group mt-10 inline-flex items-center gap-3 font-sans uppercase text-navy/85 hover:text-gold transition-colors"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          <span className="relative">
            Return to homepage
            <span
              aria-hidden="true"
              className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left transition-transform duration-300 ease-out group-hover:scale-x-100"
            />
          </span>
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="contact-form" aria-live="polite">
      {serverError ? (
        <div className="mb-8 border-t border-navy/30 pt-5" data-testid="contact-server-error">
          <span
            className="font-sans uppercase text-navy"
            style={{ fontSize: "11px", letterSpacing: "0.22em" }}
          >
            Unable to submit
          </span>
          <p className="mt-2 font-sans text-navy/80" style={{ fontSize: "14px" }}>
            {serverError}
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
        <div>
          <Label htmlFor="cf-name">Name</Label>
          <Input id="cf-name" value={form.name} onChange={set("name")} error={errors.name} data-testid="cf-name" autoComplete="name" />
          <FieldError id="cf-name-err">{errors.name}</FieldError>
        </div>
        <div>
          <Label htmlFor="cf-company">Company</Label>
          <Input id="cf-company" value={form.company} onChange={set("company")} error={errors.company} data-testid="cf-company" autoComplete="organization" />
          <FieldError id="cf-company-err">{errors.company}</FieldError>
        </div>
        <div>
          <Label htmlFor="cf-email">Email</Label>
          <Input id="cf-email" type="email" value={form.email} onChange={set("email")} error={errors.email} data-testid="cf-email" autoComplete="email" />
          <FieldError id="cf-email-err">{errors.email}</FieldError>
        </div>
        <div>
          <Label htmlFor="cf-phone">Phone <span className="lowercase tracking-normal text-navy/40">(optional)</span></Label>
          <Input id="cf-phone" type="tel" value={form.phone} onChange={set("phone")} data-testid="cf-phone" autoComplete="tel" />
        </div>
      </div>

      <div className="mt-8">
        <Label htmlFor="cf-subject">Subject</Label>
        <Input id="cf-subject" value={form.subject} onChange={set("subject")} error={errors.subject} data-testid="cf-subject" autoComplete="off" />
        <FieldError id="cf-subject-err">{errors.subject}</FieldError>
      </div>

      <div className="mt-8">
        <Label htmlFor="cf-message">Message</Label>
        <Input as="textarea" id="cf-message" rows={6} value={form.message} onChange={set("message")} error={errors.message} data-testid="cf-message" />
        <FieldError id="cf-message-err">{errors.message}</FieldError>
      </div>

      {/* Honeypot — visually hidden, not announced */}
      <div aria-hidden="true" style={{ position: "absolute", left: -10000, top: "auto", width: 1, height: 1, overflow: "hidden" }}>
        <label htmlFor="cf-website">Website</label>
        <input
          id="cf-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set("website")}
        />
      </div>

      <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <button
          type="submit"
          disabled={submitting}
          data-testid="cf-submit"
          aria-busy={submitting || undefined}
          className="group relative inline-flex items-center justify-center gap-3 px-7 py-3.5 border border-navy/70 hover:border-gold text-navy font-sans uppercase transition-colors duration-300 disabled:opacity-60 disabled:cursor-wait"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          <span className="relative">
            {submitting ? "Sending" : "Send Inquiry"}
            <span
              aria-hidden="true"
              className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left transition-transform duration-300 ease-out group-hover:scale-x-100"
            />
          </span>
          {submitting ? (
            <span
              aria-hidden="true"
              className="inline-flex items-center gap-[3px]"
              data-testid="cf-submit-spinner"
            >
              <span className="inline-block h-[3px] w-[3px] rounded-full bg-gold animate-[cfDot_1.1s_ease-in-out_infinite]" style={{ animationDelay: "0ms" }} />
              <span className="inline-block h-[3px] w-[3px] rounded-full bg-gold animate-[cfDot_1.1s_ease-in-out_infinite]" style={{ animationDelay: "180ms" }} />
              <span className="inline-block h-[3px] w-[3px] rounded-full bg-gold animate-[cfDot_1.1s_ease-in-out_infinite]" style={{ animationDelay: "360ms" }} />
            </span>
          ) : null}
        </button>
        <p className="font-sans text-navy/60 max-w-sm" style={{ fontSize: "12px", lineHeight: 1.55 }}>
          Submissions are reviewed in confidence. By submitting, you consent to be contacted regarding your inquiry.
        </p>
      </div>
    </form>
  );
};

export default ContactForm;
